import type { PortalCustomizationColors } from '@/types/nest/portal-customization'

/**
 * CSS variables that are overridden on the document root when a customer has brand colors.
 * The lzp-library derives the primary, app bar and menu bar colors from `--colours-brand-colour-1`.
 * The sidebar variables are only used by the portal's own `SideBar.vue`.
 */
const PRIMARY_VARIABLE = '--colours-brand-colour-1'
const PRIMARY_TEXT_VARIABLE = '--colours-primary-text'
const SIDEBAR_VARIABLE = '--colours-sidebar'
const SIDEBAR_TEXT_VARIABLE = '--colours-sidebar-text'

const THEME_VARIABLES = [
  PRIMARY_VARIABLE,
  PRIMARY_TEXT_VARIABLE,
  SIDEBAR_VARIABLE,
  SIDEBAR_TEXT_VARIABLE,
]

export const DEFAULT_PRIMARY_BRAND_COLOR = '#0074c2'
export const DEFAULT_SECONDARY_BRAND_COLOR = '#f7f9fa'

const LIGHT_TEXT_COLOR = '#ffffff'
const DARK_TEXT_COLOR = '#1f1f1f'

/**
 * WCAG AA minimum contrast for normal text.
 */
export const MINIMUM_CONTRAST_RATIO = 4.5

const HEX_COLOR_PATTERN = /^#[0-9a-fA-F]{6}$/

export function isHexColor(value: string | null | undefined): value is string {
  return value != null && HEX_COLOR_PATTERN.test(value)
}

/**
 * Relative luminance as defined by WCAG 2.x, between 0 (black) and 1 (white).
 */
function relativeLuminance(hexColor: string): number {
  const channels = [1, 3, 5].map(start => parseInt(hexColor.slice(start, start + 2), 16) / 255)
  const [red, green, blue] = channels.map(channel =>
    channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4
  ) as [number, number, number]

  return 0.2126 * red + 0.7152 * green + 0.0722 * blue
}

/**
 * WCAG contrast ratio between two colors, from 1 (no contrast) to 21 (black on white).
 */
export function contrastRatio(firstHexColor: string, secondHexColor: string): number {
  const first = relativeLuminance(firstHexColor)
  const second = relativeLuminance(secondHexColor)

  return (Math.max(first, second) + 0.05) / (Math.min(first, second) + 0.05)
}

/**
 * Returns white or near-black, whichever has the best contrast on the background.
 */
export function readableTextColor(backgroundHexColor: string): string {
  return contrastRatio(backgroundHexColor, LIGHT_TEXT_COLOR) >=
    contrastRatio(backgroundHexColor, DARK_TEXT_COLOR)
    ? LIGHT_TEXT_COLOR
    : DARK_TEXT_COLOR
}

/**
 * True when dark text reads better on the color than white text does.
 */
export function isLightColor(hexColor: string): boolean {
  return readableTextColor(hexColor) === DARK_TEXT_COLOR
}

/**
 * The colors that are actually shown: defaults for empty brand colors, and contrast-based text for empty
 * foreground colors.
 */
export function resolvePortalColors(
  colors: PortalCustomizationColors | null | undefined
): Record<keyof PortalCustomizationColors, string> {
  const primaryBrandColor = isHexColor(colors?.primaryBrandColor)
    ? colors.primaryBrandColor
    : DEFAULT_PRIMARY_BRAND_COLOR
  const secondaryBrandColor = isHexColor(colors?.secondaryBrandColor)
    ? colors.secondaryBrandColor
    : DEFAULT_SECONDARY_BRAND_COLOR

  return {
    primaryBrandColor,
    primaryForegroundColor: isHexColor(colors?.primaryForegroundColor)
      ? colors.primaryForegroundColor
      : readableTextColor(primaryBrandColor),
    secondaryBrandColor,
    secondaryForegroundColor: isHexColor(colors?.secondaryForegroundColor)
      ? colors.secondaryForegroundColor
      : readableTextColor(secondaryBrandColor),
  }
}

/**
 * Applies the brand colors to the whole document, so teleported modals and sheets use them too.
 * Without any colors the portal keeps the lzp-library defaults.
 */
export function applyPortalTheme(colors: PortalCustomizationColors | null | undefined): void {
  clearPortalTheme()

  const hasPrimary =
    isHexColor(colors?.primaryBrandColor) || isHexColor(colors?.primaryForegroundColor)
  const hasSecondary =
    isHexColor(colors?.secondaryBrandColor) || isHexColor(colors?.secondaryForegroundColor)

  const resolved = resolvePortalColors(colors)
  const style = document.documentElement.style

  if (hasPrimary) {
    style.setProperty(PRIMARY_VARIABLE, resolved.primaryBrandColor)
    style.setProperty(PRIMARY_TEXT_VARIABLE, resolved.primaryForegroundColor)
  }

  if (hasSecondary) {
    style.setProperty(SIDEBAR_VARIABLE, resolved.secondaryBrandColor)
    style.setProperty(SIDEBAR_TEXT_VARIABLE, resolved.secondaryForegroundColor)
  }
}

export function clearPortalTheme(): void {
  for (const variable of THEME_VARIABLES) {
    document.documentElement.style.removeProperty(variable)
  }
}
